/**
 * Every sentence the auto-schedule dialog shows. The engine returns codes and
 * minutes; turning them into words lives here, next to the translations.
 */

import { getLocale } from '../../../i18n';
import {
  addDays,
  eachDate,
  monthRange,
  weekStart,
  type BlockerCode,
  type ContractStanding,
  type DateRange,
  type GuardWeekSummary,
  type UnfilledBlock,
} from '../../../services/autoSchedule';
import {
  MINUTES_PER_DAY,
  formatAbsoluteMinute,
  formatDateKeyLabel,
  formatWeekRange,
} from '../../../utils/siteScheduling';

export type SchedulePeriod = 'day' | 'week' | 'month';

export const SCHEDULE_PERIODS: SchedulePeriod[] = ['day', 'week', 'month'];

/** Shift lengths offered, in hours. */
export const SHIFT_LENGTH_OPTIONS = [6, 8, 10, 12];
export const DEFAULT_SHIFT_HOURS = 8;

/**
 * The most site-days (sites x days) one run drafts. Each dry run and the save
 * judge every roster day the plan touches inside one request, which the RPCs
 * allow 30 seconds. Measured locally at about 5 ms a site-day (twelve sites
 * for a month: 1.8 s), and up to five times that while a small database's
 * statistics catch up, so this leaves room either way.
 */
export const MAX_SITE_DAYS = 500;

/** auto_schedule_authorize refuses more sites than this in one run. */
export const MAX_SITES = 60;

type Translate = (key: string, params?: Record<string, string | number>) => string;
type TranslatePlural = (singular: string, plural: string, count: number, params?: Record<string, string | number>) => string;

export interface Translators {
  t: Translate;
  tn: TranslatePlural;
}

/** The dates a period covers around the day the board is showing. */
export function periodRange(period: SchedulePeriod, anchorDate: string): DateRange {
  if (period === 'day') return { startDate: anchorDate, endDate: anchorDate };
  if (period === 'week') {
    const monday = weekStart(anchorDate);
    return { startDate: monday, endDate: addDays(monday, 6) };
  }
  return monthRange(anchorDate);
}

export function periodLabel(period: SchedulePeriod, range: DateRange): string {
  if (period === 'day') return formatDateKeyLabel(range.startDate);
  if (period === 'week') return formatWeekRange(eachDate(range.startDate, range.endDate));
  const [year, month] = range.startDate.split('-').map((part) => Number.parseInt(part, 10));
  return new Intl.DateTimeFormat(getLocale(), { month: 'long', year: 'numeric', timeZone: 'UTC' })
    .format(new Date(Date.UTC(year, month - 1, 1)));
}

export function periodName(period: SchedulePeriod, { t }: Translators): string {
  switch (period) {
    case 'day': return t('Day');
    case 'week': return t('Week');
    default: return t('Month');
  }
}

export const formatHours = (minutes: number): string => `${Number((minutes / 60).toFixed(1))}h`;

/** 22:00-06:00 reads as such, with the finish marked as the next day. */
export function formatShiftTime(startMinute: number, endMinute: number): string {
  const start = formatAbsoluteMinute(startMinute);
  const end = formatAbsoluteMinute(endMinute, { showMidnightAs24: endMinute === MINUTES_PER_DAY });
  return endMinute > MINUTES_PER_DAY ? `${start}-${end} +1` : `${start}-${end}`;
}

/** Why nobody took a shift, most common reason first. */
export function openShiftReasons(open: UnfilledBlock, { t, tn }: Translators): string {
  const { reasons } = open;
  if (reasons.unworkable) {
    return t('Nobody can work this shift lawfully as it stands: it is too long, or its break cannot be placed.');
  }

  const parts = (Object.entries(reasons) as Array<[BlockerCode, number]>)
    .filter(([, count]) => count > 0)
    .sort((a, b) => b[1] - a[1])
    .map(([code, count]) => blockerPhrase(code, count, tn));

  return parts.length > 0 ? parts.join(', ') : t('There are no active guards to choose from.');
}

function blockerPhrase(code: BlockerCode, count: number, tn: TranslatePlural): string {
  switch (code) {
    case 'other_site': return tn('{count} bound to other sites', '{count} bound to other sites', count);
    case 'no_licence': return tn('{count} without the licence', '{count} without the licence', count);
    case 'on_leave': return tn('{count} on leave', '{count} on leave', count);
    case 'unavailable': return tn('{count} unavailable', '{count} unavailable', count);
    case 'overlap': return tn('{count} working elsewhere', '{count} working elsewhere', count);
    case 'short_rest': return tn('{count} would be short of rest', '{count} would be short of rest', count);
    case 'consecutive_days': return tn('{count} at their limit of days in a row', '{count} at their limit of days in a row', count);
    case 'weekly_average': return tn('{count} at their weekly hours limit', '{count} at their weekly hours limit', count);
    case 'night_limit': return tn('{count} at their night-work limit', '{count} at their night-work limit', count);
    case 'rule_check': return tn('{count} refused by the rules check', '{count} refused by the rules check', count);
    default: return tn('{count} cannot work it', '{count} cannot work it', count);
  }
}

export function standingLabel(week: Pick<GuardWeekSummary, 'standing' | 'weekSettled'>, { t }: Translators): string {
  const standing: ContractStanding = week.standing;
  switch (standing) {
    case 'below_minimum': return week.weekSettled ? t('Below contract') : t('Below contract so far');
    case 'above_band': return t('Above band');
    case 'no_contract': return t('No contract hours');
    default: return t('Within band');
  }
}

export const standingTone = (week: Pick<GuardWeekSummary, 'standing' | 'weekSettled'>): 'success' | 'warning' | 'neutral' => {
  if (week.standing === 'within_band') return 'success';
  if (week.standing === 'no_contract' || (week.standing === 'below_minimum' && !week.weekSettled)) return 'neutral';
  return 'warning';
};

export function contractLabel(week: Pick<GuardWeekSummary, 'contractMinWeeklyMinutes' | 'contractMaxWeeklyMinutes'>, { t }: Translators): string {
  if (week.contractMinWeeklyMinutes === null) return t('Not set');
  if (week.contractMaxWeeklyMinutes === null) return t('No guaranteed hours');
  return t('{min}-{max}h', { min: week.contractMinWeeklyMinutes / 60, max: week.contractMaxWeeklyMinutes / 60 });
}
